<!-- This component is used on the complete step to display all of the plan holder's steps and a button to submit the plan. -->
<template>
	<div class="flex flex-col gap-4 p-5">
		<div class="flex items-center justify-between">
			<div class="flex items-center gap-3">
				<PlanAvatar
					:plan="planHolder"
					:index="index"
					:filled="true"
					:large="true"
				/>
				<span class="text-2xl font-bold">
					{{ t('document.finalizeTitle', { name: planHolder.firstName }) }}
				</span>
				<button
					v-if="sessionDocumentsStore.hasCompletedDocumentStep(planHolder.id)"
					type="button"
					:disabled="!foxitReady"
					class="text-primary-400! disabled:text-secondary-400! flex items-center gap-1 p-2 text-sm font-medium disabled:cursor-not-allowed"
					@click="$emit('preview')"
				>
					<DocumentTextIcon class="h-4 w-4" />
					{{ t('document.preview') }}
				</button>
			</div>
			<div class="flex items-center gap-2">
				<Tooltip
					element="button"
					aria-label="Policy Email"
					:contentClass="
						emailError?.type === PlanErrorType.PolicyEmail
							? 'text-left w-72'
							: 'text-left w-64'
					"
					@click="openUpdateEmailModal('policy')"
				>
					<template #trigger>
						<EnvelopeIcon
							class="size-6 shrink-0"
							:class="
								emailError?.type === PlanErrorType.PolicyEmail
									? 'fill-yellow-500! text-white!'
									: 'text-primary-400!'
							"
						/>
					</template>
					<template #content>
						<template v-if="emailError?.type === PlanErrorType.PolicyEmail">
							<p class="text-xs font-semibold text-yellow-500">
								{{ emailError?.title }}
							</p>
							<p class="text-xs text-slate-500">
								{{ emailError?.message }}
							</p>
						</template>
						<template v-else>
							<p class="text-xs font-semibold break-all text-blue-600">
								{{ planHolder.email }}
							</p>
							<p class="text-xs text-slate-500">
								{{ t('document.policyEmailHoverMessage') }}
							</p>
						</template>
					</template>
				</Tooltip>
				<!-- Always shown: a blank Everplans email is valid for a Free Everplans, and this is the only way back into the modal to fill it in later. -->
				<Tooltip
					element="button"
					aria-label="Everplans Email"
					:contentClass="
						emailError?.type === PlanErrorType.EverplansEmail
							? 'text-left w-72'
							: 'text-left w-64'
					"
					@click="openUpdateEmailModal('everplans')"
				>
					<template #trigger>
						<EverplansIcon
							class="-mt-0.5 size-5"
							:class="
								emailError?.type === PlanErrorType.EverplansEmail
									? 'text-yellow-500!'
									: 'text-primary-400!'
							"
						/>
					</template>
					<template #content>
						<template v-if="emailError?.type === PlanErrorType.EverplansEmail">
							<p class="text-xs font-semibold text-yellow-500">
								{{ emailError?.title }}
							</p>
							<p class="text-xs text-slate-500">
								{{ emailError?.message }}
							</p>
						</template>
						<template v-else>
							<p
								v-if="planHolder.everplansEmail"
								class="text-xs font-semibold break-all text-blue-600"
							>
								{{ planHolder.everplansEmail }}
							</p>
							<p
								v-else
								class="text-xs font-semibold text-slate-400! italic"
							>
								{{ t('document.noEverplansEmailYet') }}
							</p>
							<p class="text-xs text-slate-500">
								{{ t('document.everplansEmailHoverMessage') }}
							</p>
						</template>
					</template>
				</Tooltip>
			</div>
		</div>
		<UpdateEmailModal
			v-if="updateModalOpen"
			:key="`update-email-modal-${planHolder.id}`"
			:mode="modalMode"
			:planHolder="planHolder"
			@close="updateModalOpen = false"
			@saveEmail="saveEmail"
		/>
		<i18n-t
			tag="p"
			class="text-xs text-slate-500 italic"
			keypath="document.previewPolicyNote.message"
		>
			<strong>{{ t('document.previewPolicyNote.finalized') }}</strong>
			<strong>{{ t('document.previewPolicyNote.notFinalized') }}</strong>
		</i18n-t>
		<FuneralDirectorPicker
			v-if="shouldShowFuneralDirectorDropdown"
			:planHolderId="planHolder.id"
			@validity-changed="handleValidityChange"
		/>
		<div
			class="border-primary-50 flex flex-col gap-2 rounded-md border bg-slate-50 px-5 py-3"
		>
			<div class="flex flex-col">
				<div
					v-for="(step, stepIndex) in allSteps.filter(
						(s) => s.label !== 'confirmation'
					)"
					:key="`${step.label}-${stepIndex}`"
					:aria-label="step.label"
					:data-step-type="step.type"
					class="flex items-center justify-between border-b border-gray-300 px-3 py-2 last:border-b-0"
				>
					<span
						:class="
							step.completed
								? 'text-primary-700 text-sm font-bold'
								: 'text-secondary-400 text-sm font-bold'
						"
					>
						{{ t(step.title) }}
					</span>
					<div
						v-if="step.completed"
						class="flex items-center gap-2"
					>
						<CheckIcon class="text-accent-500 h-4 w-4" />
						<span class="text-accent-500 text-xs uppercase">
							{{ t('document.finalized') }}
						</span>
					</div>
				</div>
			</div>
			<!--	Add loading state of button based on status of requests -->
			<div class="mt-4 flex flex-row-reverse items-center justify-between">
				<button
					type="button"
					aria-label="Packet Submission Button"
					class="bg-accent-400! text-accent-700! disabled:text-secondary-400! flex h-12 items-center justify-center self-end justify-self-end rounded-md px-6 text-sm font-bold uppercase disabled:cursor-not-allowed disabled:bg-slate-200!"
					:disabled="!canSubmit"
					:title="
						!canSubmit && shouldShowFuneralDirectorDropdown
							? t('document.submission.invalidSignerError')
							: undefined
					"
					@click="submitPlan(planHolder)"
				>
					<template v-if="planHolder.packetStatus === PlanPacketStatus.Sending">
						<LoadingSVG
							variant="green"
							class="p-1"
						/>
					</template>
					<template v-else>
						{{ submitButtonLabel }}
					</template>
				</button>
			</div>
		</div>
		<Teleport
			defer
			to="#main-content"
		>
			<InlineModal
				v-if="paymentWarningOpen"
				v-model:open="paymentWarningOpen"
				variant="small"
			>
				<template #header>
					<div class="flex w-full items-center justify-end">
						<button
							type="button"
							@click="paymentWarningOpen = false"
						>
							<XMarkIcon class="text-secondary-300! h-6 w-6" />
						</button>
					</div>
				</template>
				<div
					class="flex flex-col items-center justify-center gap-4 text-center"
				>
					<ExclamationTriangleIcon class="mb-3 h-10 w-10 text-red-400" />
					<p class="text-secondary-900! font-bold">
						{{
							t('document.submission.incompletePayment.header', {
								step: incompletePaymentStepName,
							})
						}}
					</p>
					<p class="px-8 pb-2 text-sm font-extralight text-slate-500!">
						{{ incompletePaymentStepMessage }}
					</p>
					<button
						type="button"
						class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! w-full rounded-lg py-3 font-bold"
						@click="goToIncompletePaymentStep(planHolder)"
					>
						{{ t('document.submission.incompletePayment.updateNow') }}
					</button>
					<button
						type="button"
						class="text-accent-500! font-bold"
						@click="paymentWarningOpen = false"
					>
						{{ t('general.close') }}
					</button>
				</div>
			</InlineModal>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import Plan, { PlanErrorType } from '@/classes/Plan'
import InlineModal from '@/components/common/InlineModal.vue'
import LoadingSVG from '@/components/common/LoadingSVG.vue'
import Tooltip from '@/components/common/Tooltip.vue'
import FuneralDirectorPicker from '@/components/documents/CompleteStep/FuneralDirectorPicker.vue'
import UpdateEmailModal from '@/components/documents/CompleteStep/UpdateEmailModal.vue'
import EverplansIcon from '@/components/icons/EverplansIcon.vue'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { OapProvider } from '@/data/oapProviders'
import { DocumentType } from '@/enums/DocumentType'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { usePresentationLocale } from '@/i18n'
import { Feature, useFeatureService } from '@/services/featureService'
import type { SubmissionChannel } from '@/services/types'
import { usePlanStore } from '@/stores/plan'
import type { Step } from '@/stores/sessionDocuments'
import {
	type PageStatusType,
	useSessionDocumentsStore,
} from '@/stores/sessionDocuments'
import { useSubmissionStore } from '@/stores/submission'
import {
	CheckIcon,
	DocumentTextIcon,
	XMarkIcon,
} from '@heroicons/vue/24/outline'
import { EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { computed, onMounted, ref } from 'vue'

const props = defineProps<{
	planHolder: Plan
	index: number
	foxitReady: boolean
	emailError: {
		type: PlanErrorType
		title: string
		message: string
	} | null
}>()

const emit = defineEmits<{
	preview: []
	updateErrors: []
}>()

const { t } = usePresentationLocale()
const submissionStore = useSubmissionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const featureService = useFeatureService()
const planStore = usePlanStore()
const paymentWarningOpen = ref(false)
const updateModalOpen = ref(false)
const modalMode = ref<'policy' | 'everplans'>('policy')
const signatureServiceEnabled = featureService.isEnabled(
	Feature.RemoteSignature
)

onMounted(() => {
	emit('updateErrors')
})

const isStateRequiresSignature = sessionDocumentsStore.isStateRequiresSignature(
	props.planHolder.id
)
const incompletePaymentStep = ref<Step>()
const incompletePaymentStepName = computed(() =>
	incompletePaymentStep.value ? t(incompletePaymentStep.value.title) : undefined
)
const incompletePaymentStepMessage = computed(() => {
	return incompletePaymentStepName.value === DocumentType.PaymentAuthorization
		? t('document.submission.incompletePayment.paymentAuthorizationMessage', {
				step: incompletePaymentStepName.value,
			})
		: t('document.submission.incompletePayment.message', {
				step: incompletePaymentStepName.value,
			})
})

const isSignerValid = ref(false)
let dataNeedsToBeReset = false

const allSteps = computed(() => [
	...sessionDocumentsStore.steps[props.planHolder.id],
	...sessionDocumentsStore.supplementalSteps[props.planHolder.id],
])

// To submit, the plan holder needs to:
// - Have at least one document completed
// - Have no missing required signatures or selected signer
const canSubmit = computed(() => {
	const hasCompletedDocuments =
		props.planHolder.completedSteps
			.filter(Boolean) // Filter out nullish values
			.filter((s) => s !== 'confirmation').length > 0

	const signerNeeded = sessionDocumentsStore.planNeedsSigner(
		props.planHolder.id
	)

	const signerValid = !signerNeeded || isSignerValid.value

	const signatureRequirementsDone = signatureServiceEnabled ? signerValid : true

	return (
		hasCompletedDocuments &&
		signatureRequirementsDone &&
		!isFreemiumWithDuplicateEmailWithIncompletePremiums.value
	)
})

/**
 * We use this computed to ensure the submit button is blocked if a freemium plan has the same email as an unsubmitted premium
 * Reason being we process Everplans submission in a FIFO order.
 * Blocking the freemium allows premium to reach Everplans first, using the email to receive an invite.
 */
const isFreemiumWithDuplicateEmailWithIncompletePremiums = computed(() => {
	// This plan is Premium so condition does not apply
	if (props.planHolder.isEverplansPremium()) {
		return false
	}
	// if it is a freemium, the email should not collide with any premium plans with the same email, that has not submitted yet
	return planStore.hasDuplicateEmailWithIncompletePremiums(props.planHolder)
})

const isMissingSignatures = computed(() => {
	return sessionDocumentsStore.isMissingSignatures(props.planHolder.id)
})

const shouldValidateNglSubmission = computed(() =>
	props.planHolder.completedSteps.includes(
		DocumentType.StatementOfGoodsAndServices
	)
)

const saveEmail = (email: string) => {
	if (modalMode.value === 'policy') {
		props.planHolder.email = email
	}

	if (modalMode.value === 'everplans') {
		props.planHolder.everplansEmail = email
	}

	emit('updateErrors')
	updateModalOpen.value = false
}

const openUpdateEmailModal = (mode: 'policy' | 'everplans') => {
	modalMode.value = mode
	updateModalOpen.value = true
}

/**
 * We only show the dropdown if:
 *  Signature service is enabled
 *  AND the State requires signatures
 *  AND this form is missing signatures
 */
const shouldShowFuneralDirectorDropdown = computed(() => {
	return (
		signatureServiceEnabled &&
		isStateRequiresSignature &&
		isMissingSignatures.value
	)
})

const signerRequired = computed(
	() =>
		sessionDocumentsStore.selectedSigners[props.planHolder.id] !==
		'not-required'
)

/**
 * Whether the 'Submit' button will send a signing request or
 * submit immediately to NGL.  It mainly flips the text.
 * This is the same logic as whether we show the dropdown
 * but with the added caveat that we do not send them if
 * the user has selected the 'not required' option in the
 * dropdown.
 */
const willSendSignatures = computed<boolean>(
	() =>
		signatureServiceEnabled &&
		isStateRequiresSignature &&
		isMissingSignatures.value &&
		signerRequired.value
)

const submitButtonLabel = computed<string>(() =>
	willSendSignatures.value
		? t('document.submitDocumentsWithSignatures')
		: t('document.submitDocuments')
)

const handleValidityChange = (newValue: boolean) => {
	isSignerValid.value = newValue
}

const submissionChannelToPaymentStep: Record<
	SubmissionChannel,
	DocumentType | undefined
> = {
	NGL: DocumentType.PaymentAuthorization,
	Sepio: DocumentType.OAPEnrollment,
	Inman: DocumentType.OAPEnrollment,
	Everplans: DocumentType.EverplansEnrollment,
	Email: undefined,
}

const oapProviderToSubmissionChannel: Partial<
	Record<OapProvider, SubmissionChannel>
> = {
	[OapProvider.SEPIO_GUARD]: 'Sepio',
	[OapProvider.INMAN]: 'Inman',
}

const getServicesToCheck = (planHolder: Plan): SubmissionChannel[] => {
	const services: SubmissionChannel[] = []

	if (shouldValidateNglSubmission.value) {
		services.push('NGL')
	}

	if (planHolder.oapIncluded) {
		const provider = planHolder.oapPayment.provider
		const oapService = provider
			? oapProviderToSubmissionChannel[provider]
			: undefined
		if (oapService) {
			services.push(oapService)
		}
	}

	if (planHolder.everplansIncluded) {
		services.push('Everplans')
	}

	return services
}

const findIncompletePaymentStep = (services: SubmissionChannel[]) => {
	return allSteps.value.find(
		(step) =>
			!step.completed &&
			services.some(
				(service) => submissionChannelToPaymentStep[service] === step.label
			)
	)
}

/**
 * This function will check for incomplete payment steps and validate the submission for each service.
 * NGL validation is only required when the G&S form is complete and NGL will actually submit.
 */
const validateSubmission = (planHolder: Plan) => {
	dataNeedsToBeReset = false
	const servicesToCheck = getServicesToCheck(planHolder)

	incompletePaymentStep.value = findIncompletePaymentStep(servicesToCheck)
	if (incompletePaymentStep.value) {
		paymentWarningOpen.value = true
		return false
	}

	const invalidServices = submissionStore.validPaymentDetailsForSubmission(
		planHolder,
		servicesToCheck
	)

	if (invalidServices.length > 0) {
		const invalidStepLabel = submissionChannelToPaymentStep[invalidServices[0]]
		incompletePaymentStep.value = allSteps.value.find(
			(step) => step.label === invalidStepLabel
		)
		paymentWarningOpen.value = true
		dataNeedsToBeReset = true
		return false
	}

	return true
}

const submitPlan = async (planHolder: Plan) => {
	if (!validateSubmission(planHolder)) {
		return
	}

	await submissionStore.submitPlanPacket(planHolder)
}

const goToIncompletePaymentStep = async (planHolder: Plan) => {
	if (!incompletePaymentStep.value) {
		return
	}

	if (dataNeedsToBeReset) {
		switch (incompletePaymentStep.value.label) {
			case DocumentType.PaymentAuthorization:
				await planStore.resetInsurancePaymentDetails(planHolder)
				break
			case DocumentType.EverplansEnrollment:
				await planStore.resetEverplansPaymentDetails(planHolder)
				break
			case DocumentType.OAPEnrollment:
				await planStore.resetOapPaymentDetails(planHolder)
				break
		}
	}

	await redirectToIncompletePaymentStep(incompletePaymentStep.value)
}

const redirectToIncompletePaymentStep = async (step: Step) => {
	paymentWarningOpen.value = false
	await sessionDocumentsStore.setPageState('pending')
	await sessionDocumentsStore.setStepCompleted(step, false)
	sessionDocumentsStore.setStepActive(props.planHolder.id, step)
	await sessionDocumentsStore.setPageState(step.label as PageStatusType)
	await setInvalidStepToHtml(step)
}

// This method will set the invalid step to HTML when the user is redirected to the step
const setInvalidStepToHtml = async (step: Step) => {
	if (!step) {
		return
	}

	switch (step.label) {
		case DocumentType.PaymentAuthorization:
			await sessionDocumentsStore.setPaymentStepTo('html')
			break
		case DocumentType.EverplansEnrollment:
			await sessionDocumentsStore.setEverplansStepTo('html')
			break
		case DocumentType.OAPEnrollment:
			await sessionDocumentsStore.setOapStepTo('html')
			break
	}
}
</script>
